/**
 * ExcelImageExtractor.jsx
 * 엑셀 파일에서 사업자등록증 이미지를 추출하고 사업자번호로 파일명 변경
 */
import React, { useState, useRef, useCallback } from 'react';
import * as XLSX from 'xlsx';
import './ExcelImageExtractor.css';

const API_BASE = process.env.REACT_APP_API_URL || (
    window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
        ? `http://${window.location.hostname}:3002`
        : ''
);

function ExcelImageExtractor({ onRegistrationComplete }) {
    // State
    const [step, setStep] = useState(1); // 1: 업로드, 2: 시트선택, 3: 추출중, 4: 완료
    const [file, setFile] = useState(null);
    const [filePath, setFilePath] = useState('');
    const [sheets, setSheets] = useState([]);
    const [selectedSheet, setSelectedSheet] = useState('');
    const [customPatterns, setCustomPatterns] = useState(['사업자', '사업자등록증']);
    const [newPattern, setNewPattern] = useState('');

    // 진행 상태
    const [progress, setProgress] = useState({ current: 0, total: 0 });
    const [results, setResults] = useState(null);
    const [jobId, setJobId] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    // 폴더 모드
    const [folderMode, setFolderMode] = useState(false);
    const [folderPath, setFolderPath] = useState('E:\\vehicle-ocr-system\\basedata\\brn');
    const [folderFiles, setFolderFiles] = useState([]);
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [batchProgress, setBatchProgress] = useState({ current: 0, total: 0 });
    const [isUploadBatch, setIsUploadBatch] = useState(false); // New state for upload batch mode
    const [batchResults, setBatchResults] = useState([]);

    const fileInputRef = useRef(null);

    // 파일 선택 핸들러
    const handleFileSelect = useCallback(async (e) => {
        const files = Array.from(e.target.files || []);
        if (files.length === 0) return;

        if (files.length === 1) {
            // 기존 단일 파일 로직
            const selectedFile = files[0];
            setFile(selectedFile);
            setError('');
            setLoading(true);
            setIsUploadBatch(false); // 단일 파일은 업로드 배치가 아님

            const formData = new FormData();
            formData.append('file', selectedFile);
            formData.append('customPatterns', JSON.stringify(customPatterns));

            try {
                const response = await fetch(`${API_BASE}/api/migration/upload-excel`, {
                    method: 'POST',
                    body: formData
                });

                const data = await response.json();

                if (data.success) {
                    setFilePath(data.filePath);
                    setSheets(data.sheets);
                    setSelectedSheet(data.suggestedSheet || data.sheets[0]?.name || '');
                    setStep(2);
                } else {
                    setError(data.error);
                }
            } catch (err) {
                setError('파일 업로드 실패: ' + err.message);
            } finally {
                setLoading(false);
            }
        } else {
            // 다중 파일 업로드 모드 -> 폴더 유사 UI 사용
            setFolderMode(true);
            setIsUploadBatch(true); // 활성화
            setFolderFiles(files.map(f => ({
                name: f.name,
                path: f.name, // 업로드 전이라 경로는 이름으로 대체
                fileObj: f // 실제 파일 객체 저장
            })));
            setSelectedFiles(files.map(f => f.name));
            setBatchResults([]);
            setStep(1); // 초기 상태
        }
    }, [customPatterns]);

    // ...

    // 폴더 파일 목록 로드 (폴더 모드일 때만 동작하도록 수정 권장, 아니면 UI에서 숨김)
    // ...


    // 드래그 앤 드롭
    const handleDrop = useCallback((e) => {
        e.preventDefault();
        const droppedFiles = Array.from(e.dataTransfer.files);
        const validFiles = droppedFiles.filter(f => f.name.endsWith('.xlsx') || f.name.endsWith('.xls'));

        if (validFiles.length > 0) {
            // handleFileSelect 호출과 유사하게 처리
            const fakeEvent = { target: { files: validFiles } };
            handleFileSelect(fakeEvent);
        }
    }, [handleFileSelect]);

    // 이미지 추출 시작
    const startExtraction = useCallback(async () => {
        setStep(3);
        setProgress({ current: 0, total: 0 });
        setError('');

        try {
            const response = await fetch(`${API_BASE}/api/migration/extract-images`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    filePath,
                    sheetName: selectedSheet,
                    customPatterns
                })
            });

            const reader = response.body.getReader();
            const decoder = new TextDecoder();

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;

                const chunk = decoder.decode(value);
                const lines = chunk.split('\n');

                for (const line of lines) {
                    if (line.startsWith('data: ')) {
                        try {
                            const data = JSON.parse(line.slice(6));

                            if (data.step === 'processing') {
                                setProgress({ current: data.current, total: data.total });
                            } else if (data.step === 'extracted') {
                                setProgress({ current: 0, total: data.totalImages });
                            } else if (data.step === 'complete' || data.jobId) {
                                setResults(data);
                                setJobId(data.jobId);
                                setStep(4);
                            }
                        } catch (e) {
                            // JSON 파싱 오류 무시
                        }
                    } else if (line.startsWith('event: error')) {
                        // 다음 라인에서 에러 데이터 처리
                    }
                }
            }
        } catch (err) {
            setError('추출 실패: ' + err.message);
            setStep(2);
        }
    }, [filePath, selectedSheet, customPatterns]);

    // 결과 다운로드 (PDF 또는 ZIP)
    const handleDownload = useCallback(() => {
        if (!jobId) return;
        // 브라우저가 Content-Disposition 헤더에 따라 파일명과 형식을 처리하도록 함
        window.location.href = `${API_BASE}/api/migration/download-zip/${jobId}`;
    }, [jobId]);

    // 패턴 추가
    const addPattern = useCallback(() => {
        if (newPattern && !customPatterns.includes(newPattern)) {
            setCustomPatterns([...customPatterns, newPattern]);
            setNewPattern('');
        }
    }, [newPattern, customPatterns]);

    // 패턴 삭제
    const removePattern = useCallback((pattern) => {
        setCustomPatterns(customPatterns.filter(p => p !== pattern));
    }, [customPatterns]);

    // 폴더 파일 목록 로드
    const loadFolderFiles = useCallback(async () => {
        setLoading(true);
        setError('');

        try {
            const response = await fetch(`${API_BASE}/api/migration/list-excel-files?folderPath=${encodeURIComponent(folderPath)}`);
            const data = await response.json();

            if (data.success) {
                setFolderFiles(data.files);
                setSelectedFiles(data.files.map(f => f.path)); // 기본 전체 선택
            } else {
                setError(data.error);
            }
        } catch (err) {
            setError('폴더 조회 실패: ' + err.message);
        } finally {
            setLoading(false);
        }
    }, [folderPath]);

    // 엑셀 다운로드
    const handleExcelDownload = useCallback(() => {
        if (!batchResults || batchResults.length === 0) return;

        // 데이터 매핑
        const excelData = batchResults.map(item => ({
            '사업자등록번호(필수)': item.brn || '', // 필수: BRN
            '파일명': item.fileName,
            '상태': item.status === 'success' ? '성공' : '실패',
            '신뢰도': item.confidence || '',
            '이미지파일명': item.savedFileName || '',
            '에러메시지': item.error || ''
        }));

        // 워크시트 생성
        const ws = XLSX.utils.json_to_sheet(excelData);

        // 컬럼 너비 설정
        ws['!cols'] = [
            { wch: 20 }, // BRN
            { wch: 30 }, // 파일명
            { wch: 10 }, // 상태
            { wch: 10 }, // 신뢰도
            { wch: 30 }, // 이미지파일명
            { wch: 40 }  // 에러메시지
        ];

        // 워크북 생성 및 다운로드
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "추출결과");
        XLSX.writeFile(wb, `사업자등록증_추출결과_${new Date().toISOString().slice(0, 10)}.xlsx`);
    }, [batchResults]);

    // 단일 파일 결과 DB 등록 + 레거시 서버 SFTP 업로드
    const handleSingleDBRegister = useCallback(async () => {
        if (!results || !results.images || results.images.length === 0) {
            alert('등록할 추출 결과가 없습니다.');
            return;
        }

        const successImages = results.images.filter(img => img.brnRaw);
        if (successImages.length === 0) {
            alert('등록할 성공 항목이 없습니다.');
            return;
        }

        if (!window.confirm(`총 ${successImages.length}건을 DB에 등록하시겠습니까?\n(파일명을 상호로 사용)\n\n✅ 레거시 서버에도 파일 업로드됩니다.`)) {
            return;
        }

        let successCount = 0;
        let failCount = 0;
        let sftpSuccessCount = 0;
        let sftpFailCount = 0;

        setLoading(true);

        for (const img of successImages) {
            try {
                // 파일명에서 확장자 제거하여 상호로 사용
                const companyName = file?.name?.replace(/\.[^/.]+$/, "") || img.brnRaw;
                const groupCode = localStorage.getItem('groupCode') || sessionStorage.getItem('groupCode') || '2000';

                const payload = {
                    businessNumber: img.brnRaw,
                    companyName: companyName,
                    companyNameEng: '',
                    ceoName: '',
                    ceoNameEng: '',
                    address: '',
                    addressEng: '',
                    tel: '',
                    fax: '',
                    gwan: '',
                    refundRate: '0',
                    saleRate: '0',
                    companyType: '법인',
                    contractSeal: '',
                    groupCode: groupCode
                };

                // 1. DB에 거래처 등록
                const res = await fetch(`${API_BASE}/api/legacy/company`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload)
                });

                const json = await res.json();
                if (json.success) {
                    successCount++;
                    const companyNo = json.companyNo;

                    // 2. SFTP 업로드 (savedFileName이 있는 경우)
                    if (companyNo && img.savedFileName) {
                        try {
                            const sftpRes = await fetch(`${API_BASE}/api/legacy/upload-to-legacy`, {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({
                                    businessNumber: img.brnRaw,
                                    companyNo: companyNo,
                                    fileName: `${img.brnRaw}.png`,
                                    savedFileName: img.savedFileName
                                })
                            });

                            const sftpJson = await sftpRes.json();
                            if (sftpJson.success) {
                                sftpSuccessCount++;
                            } else {
                                sftpFailCount++;
                            }
                        } catch (sftpError) {
                            sftpFailCount++;
                            console.error(`❌ [SFTP] 업로드 오류:`, sftpError);
                        }
                    }
                } else {
                    failCount++;
                }
            } catch (e) {
                console.error('Registration failed for', img.brnRaw, e);
                failCount++;
            }
        }

        setLoading(false);
        alert(`등록 완료!\n\n📁 DB 등록: 성공 ${successCount}건, 실패 ${failCount}건\n📤 SFTP 업로드: 성공 ${sftpSuccessCount}건, 실패 ${sftpFailCount}건`);

        // 등록된 사업자번호 리스트 수집
        const registeredBrns = successImages.map(img => img.brnRaw);

        // Trigger parent refresh
        if (onRegistrationComplete) {
            onRegistrationComplete(registeredBrns);
        }
    }, [results, file, onRegistrationComplete]);

    // DB 일괄 등록 + 레거시 서버 SFTP 업로드
    const handleBatchDBRegister = useCallback(async () => {
        if (!batchResults || batchResults.length === 0) return;

        const successItems = batchResults.filter(r => r.status === 'success' && r.brn);
        if (successItems.length === 0) {
            alert('등록할 성공 항목이 없습니다.');
            return;
        }

        if (!window.confirm(`총 ${successItems.length}건을 DB에 등록하시겠습니까?\n(사업자번호 기준, 파일명을 상호로 사용)\n\n✅ 레거시 서버에도 파일 업로드됩니다.`)) {
            return;
        }

        let successCount = 0;
        let failCount = 0;
        let sftpSuccessCount = 0;
        let sftpFailCount = 0;

        setLoading(true);

        for (const item of successItems) {
            try {
                // Remove extension from filename to use as company name
                const companyName = item.fileName.replace(/\.[^/.]+$/, "");

                // 🔧 FIX: 사용자의 groupCode 가져오기
                const groupCode = localStorage.getItem('groupCode') || sessionStorage.getItem('groupCode') || '2000';

                // Ensure all fields expected by companyModel.saveCompany are present
                const payload = {
                    businessNumber: item.brn,
                    companyName: companyName, // Default to filename
                    companyNameEng: '',
                    ceoName: '',
                    ceoNameEng: '',
                    address: '',
                    addressEng: '',
                    tel: '',
                    fax: '',
                    gwan: '',
                    refundRate: '0',
                    saleRate: '0',
                    companyType: '법인',
                    contractSeal: item.savedFileName ? `uploads/migration/${item.savedFileName}` : '',
                    groupCode: groupCode // 🔧 사용자의 groupCode 전달
                };

                // 1. DB에 거래처 등록
                const res = await fetch(`${API_BASE}/api/legacy/company`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload)
                });

                const json = await res.json();
                if (json.success) {
                    successCount++;

                    // 🔧 FIX: API 응답에서 직접 companyNo 사용 (별도 조회 불필요)
                    const companyNo = json.companyNo;

                    // 🔍 디버그: SFTP 업로드 조건 확인
                    console.log(`🔍 [SFTP 조건] BRN: ${item.brn}, companyNo: ${companyNo}, savedFileName: ${item.savedFileName}`);

                    if (companyNo && item.savedFileName) {
                        // 3. 레거시 서버에 SFTP로 파일 업로드
                        try {
                            const sftpRes = await fetch(`${API_BASE}/api/legacy/upload-to-legacy`, {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({
                                    businessNumber: item.brn,
                                    companyNo: companyNo,
                                    fileName: `${item.brn}.png`,
                                    savedFileName: item.savedFileName // 백엔드가 output 폴더에서 읽음
                                })
                            });

                            const sftpJson = await sftpRes.json();
                            if (sftpJson.success) {
                                sftpSuccessCount++;
                                console.log(`✅ [SFTP] ${item.brn} 업로드 완료`);
                            } else {
                                sftpFailCount++;
                                console.warn(`⚠️ [SFTP] ${item.brn} 업로드 실패:`, sftpJson.error);
                            }
                        } catch (sftpError) {
                            sftpFailCount++;
                            console.error(`❌ [SFTP] ${item.brn} 업로드 오류:`, sftpError);
                        }
                    } else {
                        console.warn(`⚠️ [SFTP] ${item.brn} 스킵 - companyNo: ${companyNo}, savedFileName: ${item.savedFileName}`);
                    }
                } else {
                    failCount++;
                }

            } catch (e) {
                console.error('Registration failed for', item.fileName, e);
                failCount++;
            }
        }

        setLoading(false);
        alert(`등록 완료!\n\n📁 DB 등록: 성공 ${successCount}건, 실패 ${failCount}건\n📤 SFTP 업로드: 성공 ${sftpSuccessCount}건, 실패 ${sftpFailCount}건`);

        // 등록된 사업자번호 리스트 수집
        const registeredBrns = batchResults
            .filter(item => item.status === 'success' && item.brn)
            .map(item => item.brn);

        console.log('📋 [EIE DEBUG] batchResults:', batchResults);
        console.log('📋 [EIE DEBUG] registeredBrns to send:', registeredBrns);


        // Trigger parent refresh with registered BRN list
        if (onRegistrationComplete) {
            onRegistrationComplete(registeredBrns);
        }

    }, [batchResults, onRegistrationComplete]);

    // 배치 처리 (폴더/업로드 공용)
    const startBatchExtraction = useCallback(async () => {
        setBatchResults([]);
        setStep(3);
        setError('');

        const total = selectedFiles.length;
        setBatchProgress({ current: 0, total });

        const BATCH_SIZE = 5;
        let completedCount = 0;
        const finalResults = [];

        for (let i = 0; i < total; i += BATCH_SIZE) {
            const batch = selectedFiles.slice(i, i + BATCH_SIZE);

            const batchPromises = batch.map(async (fileKey) => {
                // fileKey는 File Upload 모드에선 filename, Folder 모드에선 filepath
                // folderFiles에서 해당 객체를 찾음
                const fileItem = folderFiles.find(f => f.path === fileKey || f.name === fileKey);
                const fileName = fileItem ? fileItem.name : fileKey.split(/[\\/]/).pop();

                try {
                    let targetFilePath = fileKey;

                    // 업로드 모드인 경우 (fileObj가 존재) 먼저 업로드 실행
                    if (fileItem && fileItem.fileObj) {
                        const formData = new FormData();
                        formData.append('file', fileItem.fileObj);
                        formData.append('customPatterns', JSON.stringify(customPatterns));

                        const uploadRes = await fetch(`${API_BASE}/api/migration/upload-excel`, {
                            method: 'POST',
                            body: formData
                        });
                        const uploadData = await uploadRes.json();

                        if (!uploadData.success) throw new Error(uploadData.error || 'Upload failed');
                        targetFilePath = uploadData.filePath; // 서버의 임시 경로로 교체
                    }

                    // 이미지 추출 실행
                    const response = await fetch(`${API_BASE}/api/migration/extract-images`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            filePath: targetFilePath,
                            sheetName: null,
                            customPatterns
                        })
                    });

                    if (!response.ok) {
                        const errorText = await response.text();
                        console.error(`API Error for ${fileName}:`, errorText);
                        throw new Error(`Server returned ${response.status}: ${errorText.substring(0, 100)}`);
                    }

                    const reader = response.body.getReader();
                    const decoder = new TextDecoder();
                    let resultData = null;
                    let lastError = null;
                    let buffer = '';

                    let fullResponse = '';

                    while (true) {
                        const { done, value } = await reader.read();
                        if (done) break;
                        fullResponse += decoder.decode(value, { stream: true });
                    }

                    // 전체 응답 수신 후 파싱 (청크 문제 방지)
                    const lines = fullResponse.split('\n');
                    for (const line of lines) {
                        if (line.trim().startsWith('data: ')) {
                            try {
                                const jsonStr = line.trim().slice(6);
                                const d = JSON.parse(jsonStr);
                                if (d.error) lastError = d.error;
                                if (d.step === 'complete') {
                                    console.log(`✅ [배치] 완료 이벤트 파싱 성공 (${fileName}):`, JSON.stringify(d));
                                    resultData = d;
                                }
                            } catch (e) {
                                console.warn(`⚠️ [배치] JSON 파싱 오류 (${fileName}):`, e);
                            }
                        }
                    }

                    if (resultData) {
                        // 데이터 구조 호환성 처리 (onProgress vs Final Response)
                        const images = resultData.images || (resultData.results && resultData.results.images);

                        if (images && images.length > 0) {
                            const img = images[0];
                            console.log(`🖼️ [배치] 이미지 처리 결과 (${fileName}):`, img);
                            return {
                                fileName,
                                filePath: fileKey, // 원본 Key 유지
                                status: img.brnRaw ? 'success' : 'failed',
                                error: img.brnRaw ? null : 'BRN 추출 실패 (AI 인식실패)',
                                brn: img.brnRaw,
                                confidence: img.confidence,
                                savedFileName: `${img.brnRaw || 'unknown'}.${img.extension}`,
                                jobId: resultData.jobId || (resultData.results && resultData.results.jobId)
                            };
                        }
                    }

                    console.warn(`❌ [배치] 실패 로직 (${fileName}). 수신 데이터:`, JSON.stringify(resultData));
                    if (resultData) {
                        return { fileName, filePath: fileKey, status: 'failed', error: '이미지 없음 (추출된 이미지 0개)' };
                    } else {
                        return { fileName, filePath: fileKey, status: 'failed', error: lastError || '응답 없음 (시간초과 또는 스트림 오류)' };
                    }

                } catch (err) {
                    return { fileName, filePath: fileKey, status: 'failed', error: err.message };
                }
            });

            const results = await Promise.all(batchPromises);
            finalResults.push(...results);
            completedCount += batch.length;
            setBatchProgress({ current: Math.min(completedCount, total), total });
            setBatchResults(prev => [...prev, ...results]);
        }
        setStep(4);
    }, [selectedFiles, folderFiles, customPatterns]);

    // 초기화
    const reset = useCallback(() => {
        setStep(1);
        setFile(null);
        setFilePath('');
        setSheets([]);
        setSelectedSheet('');
        setProgress({ current: 0, total: 0 });
        setResults(null);
        setJobId('');
        setError('');
    }, []);

    return (
        <div className="excel-extractor">
            <div className="extractor-header">
                <h2>📂 Excel 사업자등록증 추출</h2>
                <p>엑셀 파일에서 사업자등록증 이미지를 추출하고 사업자번호로 파일명을 변경합니다.</p>

                <div className="mode-toggle">
                    <button
                        className={!folderMode ? 'active' : ''}
                        onClick={() => setFolderMode(false)}
                    >
                        📄 파일 직접 선택 (다중 가능)
                    </button>
                    <button
                        className={folderMode ? 'active' : ''}
                        onClick={() => { setFolderMode(true); loadFolderFiles(); }}
                    >
                        📁 폴더 일괄
                    </button>
                </div>
            </div>

            {error && (
                <div className="error-message">
                    ⚠️ {error}
                    <button onClick={() => setError('')}>✕</button>
                </div>
            )}

            {!folderMode ? (
                // 단일 파일 모드
                <div className="single-file-mode">
                    {/* Step 1: 파일 업로드 */}
                    {step === 1 && (
                        <div
                            className="upload-zone"
                            onDrop={handleDrop}
                            onDragOver={(e) => e.preventDefault()}
                            onClick={() => fileInputRef.current?.click()}
                        >
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept=".xlsx,.xls"
                                multiple
                                onChange={handleFileSelect}
                                style={{ display: 'none' }}
                            />

                            {loading ? (
                                <div className="loading">
                                    <div className="spinner"></div>
                                    <p>파일 분석 중...</p>
                                </div>
                            ) : (
                                <>
                                    <div className="upload-icon">📊</div>
                                    <p>Excel 파일을 드래그하거나 클릭하여 선택</p>
                                    <span className="hint">지원 형식: .xlsx, .xls</span>
                                </>
                            )}
                        </div>
                    )}

                    {/* Step 2: 시트 선택 */}
                    {step === 2 && (
                        <div className="sheet-selection">
                            <div className="file-info">
                                <span className="file-icon">📄</span>
                                <span className="file-name">{file?.name}</span>
                                <button className="change-file" onClick={reset}>변경</button>
                            </div>

                            <div className="sheet-list">
                                <h4>시트 선택</h4>
                                <select
                                    value={selectedSheet}
                                    onChange={(e) => setSelectedSheet(e.target.value)}
                                >
                                    {sheets.map((sheet, idx) => (
                                        <option key={idx} value={sheet.name}>
                                            {sheet.name} ({sheet.rowCount}행)
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="pattern-config">
                                <h4>시트 자동 감지 패턴</h4>
                                <div className="pattern-tags">
                                    {customPatterns.map((pattern, idx) => (
                                        <span key={idx} className="pattern-tag">
                                            {pattern}
                                            <button onClick={() => removePattern(pattern)}>×</button>
                                        </span>
                                    ))}
                                </div>
                                <div className="pattern-input">
                                    <input
                                        type="text"
                                        value={newPattern}
                                        onChange={(e) => setNewPattern(e.target.value)}
                                        placeholder="새 패턴 추가"
                                        onKeyPress={(e) => e.key === 'Enter' && addPattern()}
                                    />
                                    <button onClick={addPattern}>추가</button>
                                </div>
                            </div>

                            <button className="start-btn" onClick={startExtraction}>
                                🚀 이미지 추출 시작
                            </button>
                        </div>
                    )}

                    {/* Step 3: 추출 중 */}
                    {step === 3 && (
                        <div className="extraction-progress">
                            <div className="progress-icon">⚙️</div>
                            <h3>이미지 추출 중...</h3>
                            <div className="progress-bar">
                                <div
                                    className="progress-fill"
                                    style={{ width: `${progress.total ? (progress.current / progress.total) * 100 : 0}%` }}
                                ></div>
                            </div>
                            <p>{progress.current} / {progress.total} 이미지 처리됨</p>
                        </div>
                    )}

                    {/* Step 4: 완료 */}
                    {step === 4 && results && (
                        <div className="extraction-complete">
                            <div className="success-icon">✅</div>
                            <h3>추출 완료!</h3>

                            <div className="stats">
                                <div className="stat">
                                    <span className="stat-value">{results.totalImages}</span>
                                    <span className="stat-label">전체</span>
                                </div>
                                <div className="stat success">
                                    <span className="stat-value">{results.successImages}</span>
                                    <span className="stat-label">성공</span>
                                </div>
                                <div className="stat failed">
                                    <span className="stat-value">{results.failedImages}</span>
                                    <span className="stat-label">실패</span>
                                </div>
                            </div>

                            {results.images && results.images.length > 0 && (
                                <div className="result-list">
                                    <h4>추출 결과</h4>
                                    <div className="result-grid">
                                        {results.images.slice(0, 20).map((img, idx) => (
                                            <div key={idx} className={`result-item ${img.brnRaw ? 'success' : 'failed'}`}>
                                                <span className="brn">{img.brnRaw || 'N/A'}</span>
                                                <span className="confidence">{img.confidence}</span>
                                            </div>
                                        ))}
                                        {results.images.length > 20 && (
                                            <div className="more-items">+{results.images.length - 20}개 더</div>
                                        )}
                                    </div>
                                </div>
                            )}

                            <div className="action-buttons">
                                <button
                                    className="register-btn"
                                    onClick={handleSingleDBRegister}
                                    disabled={loading || !results?.images?.some(img => img.brnRaw)}
                                    style={{ background: '#28a745', color: '#fff', marginRight: '10px' }}
                                >
                                    {loading ? '⏳ 등록중...' : '📝 DB 등록'}
                                </button>
                                <button className="download-btn" onClick={handleDownload}>
                                    📦 결과 다운로드
                                </button>
                                <button className="reset-btn" onClick={reset}>
                                    🔄 새로 시작
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            ) : (
                // 폴더 일괄 모드
                <div className="folder-mode">
                    {/* 업로드 모드가 아닐 때만 폴더 입력창 표시 */}
                    {!isUploadBatch && (
                        <div className="folder-input">
                            <label>폴더 경로:</label>
                            <input
                                type="text"
                                value={folderPath}
                                onChange={(e) => setFolderPath(e.target.value)}
                                placeholder="E:\vehicle-ocr-system\basedata\brn"
                            />
                            <button onClick={loadFolderFiles} disabled={loading}>
                                {loading ? '로딩...' : '📂 불러오기'}
                            </button>
                        </div>
                    )}

                    {/* 업로드 모드일 때는 취소 헤더 표시 */}
                    {isUploadBatch && folderFiles.length > 0 && (
                        <div className="upload-batch-header" style={{ marginBottom: '15px', padding: '12px', background: '#f8f9fa', borderRadius: '6px', border: '1px solid #e9ecef', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <strong style={{ color: '#495057' }}>📤 업로드 목록 ({folderFiles.length}개 파일)</strong>
                            <button
                                style={{ padding: '4px 12px', fontSize: '0.85rem', background: '#fff', border: '1px solid #ced4da', borderRadius: '4px', cursor: 'pointer', color: '#495057' }}
                                onClick={() => {
                                    setIsUploadBatch(false);
                                    setFolderMode(false);
                                    setFolderFiles([]);
                                }}
                            >
                                ✕ 취소후 단일 선택
                            </button>
                        </div>
                    )}

                    {folderFiles.length > 0 && (
                        <div className="folder-files">
                            <div className="files-header">
                                <span>총 {folderFiles.length}개 파일</span>
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={selectedFiles.length === folderFiles.length && folderFiles.length > 0}
                                        onChange={(e) => {
                                            if (e.target.checked) {
                                                setSelectedFiles(folderFiles.map(f => f.path));
                                            } else {
                                                setSelectedFiles([]);
                                            }
                                        }}
                                    />
                                    전체 선택
                                </label>
                            </div>

                            {step === 3 && (
                                <div className="batch-progress">
                                    <div className="progress-bar">
                                        <div
                                            className="progress-fill"
                                            style={{ width: `${(batchProgress.current / batchProgress.total) * 100}%` }}
                                        ></div>
                                    </div>
                                    <p>{batchProgress.current} / {batchProgress.total} 처리 중...</p>
                                </div>
                            )}

                            {step === 4 && batchResults.length > 0 && (
                                <div className="batch-complete">
                                    <div className="summary">
                                        <span className="success">성공: {batchResults.filter(r => r.status === 'success').length}</span>
                                        <span className="failed">실패: {batchResults.filter(r => r.status === 'failed').length}</span>
                                    </div>
                                    <button className="excel-btn" onClick={handleExcelDownload}>
                                        📊 엑셀 결과 다운로드
                                    </button>
                                    <button className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 font-bold shadow transition-colors flex items-center gap-2" onClick={handleBatchDBRegister}>
                                        💾 DB 일괄 등록
                                    </button>
                                </div>
                            )}

                            <div className="file-list">
                                {folderFiles.map((file, idx) => {
                                    const result = batchResults.find(r => r.filePath === file.path);
                                    return (
                                        <label key={idx} className={`file-item ${result ? result.status : ''}`}>
                                            <input
                                                type="checkbox"
                                                checked={selectedFiles.includes(file.path)}
                                                onChange={(e) => {
                                                    if (e.target.checked) {
                                                        setSelectedFiles([...selectedFiles, file.path]);
                                                    } else {
                                                        setSelectedFiles(selectedFiles.filter(p => p !== file.path));
                                                    }
                                                }}
                                                disabled={step === 3}
                                            />
                                            <span className="file-name">{file.name}</span>
                                            {result && (
                                                <span className="result-badges">
                                                    {result.status === 'success' ? (
                                                        <span className="badge success">{result.brn || 'BRN 없음'}</span>
                                                    ) : (
                                                        <span className="badge failed" title={result.error || '알 수 없는 오류'}>
                                                            {result.error && result.error.includes('이미지 있음') ? '인식실패' : '오류'}
                                                        </span>
                                                    )}
                                                </span>
                                            )}
                                        </label>
                                    );
                                })}
                            </div>

                            {step !== 3 && step !== 4 && (
                                <button
                                    className="start-btn"
                                    onClick={startBatchExtraction}
                                    disabled={selectedFiles.length === 0}
                                >
                                    🚀 {selectedFiles.length}개 파일 일괄 추출
                                </button>
                            )}

                            {step === 4 && (
                                <button className="reset-btn" onClick={() => { setStep(1); setBatchResults([]); }}>
                                    🔄 다시 선택
                                </button>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default ExcelImageExtractor;
